import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";

import { Link, useNavigate } from "react-router-dom";

import IncidentsTable from "./IncidentsTable";
import "./Incidents.sass";
import { useAuth } from "../../contexts/AuthContext";

import { toast } from "react-toastify";

const statusCode = {
  1: "Aberta",
  2: "Encerrada",
  3: "Cancelada",
};

function ContractSelect({ data, setContractFilter }) {
  const incidents = data;
  const uniqueContracts = new Set();
  incidents.forEach((incident) => {
    uniqueContracts.add(
      `${incident["_c2g_contrato_value"]}_${incident["_c2g_contrato_value@OData.Community.Display.V1.FormattedValue"]}`
    );
  });
  return (
    <select
      name="contract-filter"
      id="contract-filter"
      onChange={(event) => {
        setContractFilter(event.target.value);
      }}
      defaultValue={"all"}
    >
      <option value="all">Todos</option>
      {[...uniqueContracts].map((uniqueContract, index) => {
        const [id, name] = uniqueContract.split("_");
        return (
          <option key={index} value={id}>
            {name}
          </option>
        );
      })}
    </select>
  );
}

function StatusSelect({ data, setStatusFilter, statusFilter }) {
  const incidents = data;
  const uniqueStatuses = new Set();
  incidents.forEach((incident) => {
    if (incident["statusnewid"] < 3) {
      uniqueStatuses.add(
        `${incident["statusnewid"]}_${incident["statusnewname"]}`
      );
    }
  });
  return (
    <select
      name="status-filter"
      id="status-filter"
      onChange={(event) => {
        setStatusFilter(event.target.value);
      }}
      value={statusFilter}
    >
      <option value="all">Todos</option>
      {[...uniqueStatuses].map((uniqueStatus, index) => {
        const [id, name] = uniqueStatus.split("_");
        return (
          <option key={index} value={id}>
            {name + "s"}
          </option>
        );
      })}
    </select>
  );
}

function Incidents() {
  const [openModal, setOpen] = useState(false);
  const handleOpenModal = () => setOpen(true);
  const handleCloseModal = () => setOpen(false);

  const [openModalWarningSeverity, setOpenWarningSeverity] = useState(false);
  const handleOpenModalSeverity = () => setOpenWarningSeverity(true);
  const handleCloseModalSeverity = () => {
    setOpenWarningSeverity(false);
    // Reseta para uma severidade permitida após fechar o modal de aviso
    const defaultSeverityId = severities.find(
      (sev) =>
        !sev.name.toLowerCase().includes("importante") &&
        !sev.name.toLowerCase().includes("emergencial")
    )?.id;

    if (defaultSeverityId) {
      setSeverity(defaultSeverityId);
    }
  };

  const [severities, setSeverities] = useState([]);
  const [uniqueContracts, setUniqueContracts] = useState([]);

  const [title, setTitle] = useState(undefined);
  const [titleCharCount, setTitleCharCount] = useState(0);
  const [severity, setSeverity] = useState("");
  const [description, setDescription] = useState(undefined);
  const [charCount, setCharCount] = useState(0);
  const [contract, setContract] = useState();

  const [statusFilter, setStatusFilter] = useState(1);
  const [contractFilter, setContractFilter] = useState("all");

  const [incidentsData, setData] = useState([]);
  const [dataLoadError, setDataLoadError] = useState(null);

  const [refreshData, setRefreshData] = useState(false);

  const navigate = useNavigate();

  const onRefreshData = async () => {
    toast.success("Visualização atualizada", {
      theme: "colored",
      autoClose: 3000,
    });

    setRefreshData(new Date());
  };

  const onSubmiIncident = async (e) => {
    e.preventDefault();

    const trimmedTitle = (title || "").trim();
    const trimmedDescription = (description || "").trim();

    if (trimmedTitle === "" || trimmedDescription === "") {
      toast.info("Preencha o título e a descrição da ocorrência", {
        theme: "colored",
      });
      return;
    }

    const id = toast.info("Criando ocorrência...", {
      theme: "colored",
      autoClose: false,
    });

    const body = {
      title: trimmedTitle,
      description: trimmedDescription,
      severity,
      contract,
    };

    const token = localStorage.getItem("token");

    const api = axios.create({
      baseURL: process.env.REACT_APP_API_URL,
      headers: {
        Authorization: token,
      },
    });

    api
      .post("incident/create", body)
      .then((response) => {
        toast.update(id, {
          render: response.data.msg,
          isLoading: false,
          type: "success",
          autoClose: 3000,
        });

        setOpen(false);
      })
      .catch((error) => {
        const errorMsg = error.response
          ? error.response.data.errors?.[0]?.message ||
            error.response.data.error ||
            error.response.data.message
          : "Erro interno no servidor, contate um administrador";

        toast.update(id, {
          render: errorMsg,
          isLoading: false,
          type: "error",
        });

        console.log("Error creating incident:");
        console.log(error);
      });
  };

  const filteredTable = useMemo(() => {
    return incidentsData.filter((item) => {
      if (contractFilter === "all" && statusFilter === "all") return true;

      if (contractFilter != "all") {
        if (statusFilter != "all") {
          return item.statusnewid == statusFilter &&
            item["_c2g_contrato_value"] == contractFilter
            ? true
            : false;
        } else {
          return item["_c2g_contrato_value"] == contractFilter ? true : false;
        }
      } else {
        if (statusFilter != "all") {
          return item.statusnewid == statusFilter ? true : false;
        }
      }
    });
  }, [incidentsData, contractFilter, statusFilter]);

  useEffect(() => {
    const token = localStorage.getItem("token") || 0;

    const api = axios.create({
      baseURL: process.env.REACT_APP_API_URL,
      headers: {
        Authorization: token,
      },
    });

    api
      .get("incident")
      .then((response) => {
        if (response.data.length > 0) {
          const sortedData = response.data.sort((a, b) => {
            const dateA = new Date(a["createdon"]);
            const dateB = new Date(b["createdon"]);

            return dateB - dateA;
          });

          setData(
            sortedData.map((incident) => ({
              ...incident,
              statusnewid:
                incident[
                  "statecode@OData.Community.Display.V1.FormattedValue"
                ] == "Ativo"
                  ? 1
                  : incident[
                      "statecode@OData.Community.Display.V1.FormattedValue"
                    ] == "Resolvido"
                  ? 2
                  : 3,
              statusnewname:
                incident[
                  "statecode@OData.Community.Display.V1.FormattedValue"
                ] == "Ativo"
                  ? "Aberta"
                  : incident[
                      "statecode@OData.Community.Display.V1.FormattedValue"
                    ] == "Resolvido"
                  ? "Encerrada"
                  : "Cancelada",
            }))
          );

          const uniqueContractsSet = new Set();

          response.data.forEach((incident) => {
            uniqueContractsSet.add(
              `${incident["_c2g_contrato_value"]}_${incident["_c2g_contrato_value@OData.Community.Display.V1.FormattedValue"]}`
            );
          });

          setUniqueContracts(uniqueContractsSet);
          setContract([...uniqueContractsSet][0].split("_")[0]);
        } else {
          setDataLoadError("No incidents");
        }
      })
      .catch((error) => {
        console.log("Error loading Incidents:");
        const errorMsg = error.response
          ? error.response.data.error
          : "Erro interno no servidor, contate um administrador";

        if (error.response.status == 401) {
          navigate("/login");
        } else {
          toast.error(errorMsg, {
            theme: "colored",
            autoClose: 10000,
          });
        }

        setDataLoadError(true);
      });
  }, [openModal, refreshData]);

  useEffect(() => {
    const token = localStorage.getItem("token");

    const api = axios.create({
      baseURL: process.env.REACT_APP_API_URL,
      headers: {
        Authorization: token,
      },
    });

    api.get("severity").then((response) => {
      setSeverities(response.data);

      const defaultSeverityId = response.data.filter((severity) => {
        return severity.isDefaultValue ? true : false;
      })[0].id;

      setSeverity(defaultSeverityId);
    });
  }, [openModal]);

  const handleSeveritySelection = (value) => {
    const selected = severities.find((sev) => sev.id === value);

    if (
      selected &&
      (selected.name.toLowerCase().includes("importante") ||
        selected.name.toLowerCase().includes("emergencial"))
    ) {
      // Não permite a seleção e abre o modal de aviso
      handleOpenModalSeverity();

      // Não atualiza o valor do select para a severidade não permitida
      return;
    }

    // Se chegou aqui, a severidade é permitida
    setSeverity(value);
  };

  return (
    <>
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className="box-modal form-wrapper">
          <form onSubmit={onSubmiIncident}>
            <div className="form-header">
              <span className="font text-4">Abertura de Ocorrência</span>
            </div>
            <div className="form-content">
              <label
                htmlFor="title"
                className={
                  "font text-2 " +
                  (titleCharCount == 200 ? "alert-max-length" : "")
                }
              >
                Título ({titleCharCount}/200)
              </label>
              <input
                type="text"
                name="title"
                id="title"
                maxlength="200"
                required
                onChange={(event) => {
                  setTitle(event.target.value);
                  setTitleCharCount(event.target.value.length);
                }}
              />

              <label htmlFor="severity" className="font text-2">
                Severidade
              </label>
              <select
                name="severity"
                id="severity"
                value={severity}
                onChange={(event) => {
                  handleSeveritySelection(event.target.value);
                }}
              >
                {severities.map((severityItem, index) => {
                  const { id, name } = severityItem;
                  return (
                    <option key={index} value={id}>
                      {name}
                    </option>
                  );
                })}
              </select>

              <label
                htmlFor="description"
                className={
                  "font text-2 " + (charCount == 2000 ? "alert-max-length" : "")
                }
              >
                Descrição do Problema ({charCount}/2000)
              </label>
              <textarea
                className="font text-1"
                name="description"
                id="description"
                cols="30"
                maxlength="2000"
                required
                onChange={(event) => {
                  setDescription(event.target.value);
                  setCharCount(event.target.value.length);
                }}
              ></textarea>

              <label htmlFor="contract" className="font text-2">
                Contrato
              </label>
              <select
                name="contract"
                id="contract"
                onChange={(event) => {
                  setContract(event.target.value);
                }}
              >
                {[...uniqueContracts].map((uniqueContract, index) => {
                  const [id, name] = uniqueContract.split("_");
                  return (
                    <option key={index} value={id}>
                      {name}
                    </option>
                  );
                })}
              </select>
              <input type="submit" value="Enviar" className="action-btn" />
            </div>
          </form>
          <div className="aside-img-div">
            <img
              src="/assets/icons/close.svg"
              alt=""
              className="top-form-icon icon"
              onClick={handleCloseModal}
            />
            <img src="/assets/images/logo.png" className="bottom-form-logo" />
          </div>
        </Box>
      </Modal>
      <Modal
        open={openModalWarningSeverity}
        onClose={handleCloseModalSeverity}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className="box-modal form-wrapper">
          <form>
            <div className="form-header">
              <span className="font text-4">Aviso</span>
            </div>
            <div className="form-content">
              <p className="font text-2">
                <b>Prezado Cliente,</b> <br />
                Para agilizar o atendimento referente a esta demanda,
                solicitamos a gentileza de entrar em contato com a nossa Central
                de Serviços pelo número <b>(51) 3303-7007</b>{" "}
              </p>
            </div>
          </form>
          <div className="aside-img-div">
            <img
              src="/assets/icons/close.svg"
              alt=""
              className="top-form-icon icon"
              onClick={handleCloseModalSeverity}
            />
            <img src="/assets/images/logo.png" className="bottom-form-logo" />
          </div>
        </Box>
      </Modal>
      <div id="content-wrapper">
        <div id="top-actions">
          <div className="action" id="addContractBtn" onClick={handleOpenModal}>
            <img src="/assets/icons/add.svg" alt="" className="icon" />
            <span className="font text-2">Cadastrar ocorrência</span>
          </div>
          <div className="action" id="refreshScreenBtn" onClick={onRefreshData}>
            <img src="/assets/icons/refresh.svg" alt="" className="icon" />
            <span className="font text-2">Atualizar</span>
          </div>
        </div>
        <div id="table-header">
          <div className="table-header-title">
            <span className="text-4 font">
              <strong>Acompanhamento de ocorrências</strong>
            </span>
          </div>

          <div id="filter-options">
            <div className="filter-item">
              <span className="text-1 font">Contrato</span>
              <ContractSelect
                data={incidentsData}
                setContractFilter={setContractFilter}
              />
            </div>
            <div className="filter-item">
              <span className="text-1 font">Situação</span>
              <StatusSelect
                data={incidentsData}
                setStatusFilter={setStatusFilter}
                statusFilter={statusFilter}
              />
            </div>
          </div>
        </div>
        <IncidentsTable data={filteredTable} error={dataLoadError} />
      </div>
    </>
  );
}

export default Incidents;
